// Central role helpers.
//
// The backend sends roles as strings ("admin", "user", "Community_user"). All
// role checks should go through these helpers instead of comparing the raw
// string, so behaviour stays consistent and new roles are easy to slot in.
//
// The user side is defined by exclusion, NOT by an allowlist: every role that
// is not "admin" gets the full user-side app. Admins can create new roles from
// User Management, and a user in one of those roles must land in a working app
// without a code change here — an allowlist silently stripped Settings,
// Favourites, notifications and the rest from any role it had not heard of.
//
// Roles that need to differ from a regular user get their own narrow helper and
// are gated at the call site:
//   - `isCommunityUser`  — "Community_user"; no distinct UI today.
//   - `isLifetimeOgUser` — "Lifetime OG Users"; unlimited tokens, so nothing
//                          token-purchase related is shown to them.
//   - `isOgUser`         — "OG Users"; a badge only, otherwise a regular user.
// Everything else about those roles still comes from `isUserSideRole`.

export const ADMIN_ROLE = "admin";
export const USER_ROLE = "user";
export const COMMUNITY_USER_ROLE = "community_user";
export const LIFETIME_OG_ROLE = "Lifetime OG Users";
export const OG_ROLE = "OG Users";

const normalize = (role?: string | null): string =>
  (role ?? "").trim().toLowerCase();

// The OG roles reach the client under several spellings — the user-details API
// sends the label ("Lifetime OG Users") while the admin APIs use the bucket key
// ("lifetimeOgUsers") — so match on an alphanumeric-only form that collapses
// all of them.
const compact = (role?: string | null): string =>
  (role ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const LIFETIME_OG_ALIASES = new Set([
  "lifetimeog",
  "lifetimeoguser",
  "lifetimeogusers",
]);

const OG_ALIASES = new Set(["og", "oguser", "ogusers"]);

// True for the admin role only.
export const isAdminRole = (role?: string | null): boolean =>
  normalize(role) === ADMIN_ROLE;

// True for the community user role only.
export const isCommunityUser = (role?: string | null): boolean =>
  normalize(role) === COMMUNITY_USER_ROLE;

// True for the lifetime OG role only — users with unlimited tokens.
export const isLifetimeOgUser = (role?: string | null): boolean =>
  LIFETIME_OG_ALIASES.has(compact(role));

// True if a user object or role list contains the Lifetime OG Users role.
export const hasLifetimeOgRole = (
  userRoles?: string[] | null,
  roleName?: string | null
): boolean => {
  if (roleName && isLifetimeOgUser(roleName)) return true;
  if (Array.isArray(userRoles) && userRoles.some((r) => isLifetimeOgUser(r)))
    return true;
  return false;
};

// True for the OG role only — users on a one-year grant of free tokens. This is
// NOT lifetime OG: they keep the normal token UI (balance, purchase, Tokens
// page) and only gain the header badge.
export const isOgUser = (role?: string | null): boolean =>
  OG_ALIASES.has(compact(role));

// True for any role that should see the user-side app — i.e. anything that is
// not an admin, including roles created from User Management after this code
// shipped. Use this wherever the code previously checked `role === "user"`.
//
// An unknown role is deliberately treated as user-side; only a missing role is
// not, so nothing user-specific renders in the moment before the role loads.
export const isUserSideRole = (role?: string | null): boolean =>
  normalize(role) !== "" && !isAdminRole(role);
